import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { UploadSimple, FileText, Sparkle, Trash, PencilSimple, User, UserSquare, Check, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Import PDF/DOCX contract → AI parse → editable preview with placeholders
 * marked as {{field_key}}. Emits `onSaved(template)` when the user confirms.
 */
export default function ImportContractDialog({ open, onOpenChange, onSaved }) {
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | parsing | edit | saving
  const [parsed, setParsed] = useState(null);
  const [name, setName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingHtml, setEditingHtml] = useState(false);

  const reset = () => {
    setStep('upload');
    setParsed(null);
    setName('');
    setUploadError('');
    setEditingKey(null);
    setEditingHtml(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File troppo grande (max 10MB)');
      return;
    }
    const nameLower = file.name.toLowerCase();
    if (!nameLower.endsWith('.pdf') && !nameLower.endsWith('.docx')) {
      setUploadError('Formato non supportato — carica un PDF o un file Word (.docx)');
      return;
    }
    setUploadError('');
    setStep('parsing');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/templates/import`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      setParsed(res.data);
      setName(res.data.suggested_name || 'Contratto importato Personale');
      setStep('edit');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Errore durante l\'importazione';
      setUploadError(typeof msg === 'string' ? msg : 'Errore durante l\'importazione');
      setStep('upload');
    }
  };

  const updateField = (key, patch) => {
    setParsed((p) => ({
      ...p,
      fields: p.fields.map((f) => (f.key === key ? { ...f, ...patch } : f)),
    }));
  };

  const deleteField = (key) => {
    // Remove field metadata AND strip placeholder from html so the user
    // doesn't end up with dangling {{key}} in the rendered contract.
    setParsed((p) => ({
      ...p,
      html: p.html.replaceAll(`{{${key}}}`, ''),
      fields: p.fields.filter((f) => f.key !== key),
    }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Dai un nome al template');
      return;
    }
    setStep('saving');
    // Store as a special "imported" template so it doesn't clash with the
    // section-based custom_templates. The content is a JSON string that
    // NewContractPage can detect via the `type: "imported"` key. Includes
    // translations (ITA/ENG/FRA/SPA/TED) generated by Gemini at import.
    const content = JSON.stringify({
      type: 'imported',
      html: parsed.html,
      fields: parsed.fields,
      source_lang: parsed.source_lang || 'ITA',
      translations: parsed.translations || {},
    });
    try {
      const res = await axios.post(`${API}/templates`, {
        name: name.trim(),
        language: parsed.source_lang || parsed.language || 'ITA',
        content,
      }, { withCredentials: true });
      const langCount = Object.keys(parsed.translations || {}).length;
      toast.success(`Template "${name.trim()}" salvato in ${langCount || 1} lingue!`);
      onSaved && onSaved(res.data);
      handleClose(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel salvataggio');
      setStep('edit');
    }
  };

  // Render the HTML with highlighted placeholders.
  const renderPreview = () => {
    if (!parsed) return null;
    // Wrap each {{key}} occurrence with a span so it looks like a chip.
    const html = parsed.html.replace(
      /\{\{([a-z0-9_]+)\}\}/gi,
      (_, key) => {
        const field = parsed.fields.find((f) => f.key === key);
        const role = field?.assigned_to || 'host';
        const color = role === 'guest' ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-amber-100 text-amber-800 border-amber-300';
        const label = field?.label || key;
        return `<span class="inline-block px-1.5 py-0.5 mx-0.5 text-[11px] font-bold rounded border ${color}" data-field-key="${key}">${label}</span>`;
      },
    );
    return <div className="prose prose-sm max-w-none text-zinc-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="import-contract-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100">
          <DialogTitle className="text-xl font-black flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-teal-600" />
            Importa il tuo contratto (PDF o Word)
          </DialogTitle>
          <p className="text-sm text-zinc-500 mt-1">
            Carica il contratto del tuo commercialista: l'AI lo ricostruisce, individua i campi da compilare e crea la tua versione personale.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <AnimatePresence mode="wait">
            {step === 'upload' && (
              <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-zinc-300 hover:border-teal-500 rounded-2xl p-8 text-center cursor-pointer transition-colors bg-zinc-50/40 hover:bg-teal-50/30"
                  data-testid="import-dropzone"
                >
                  <UploadSimple size={40} className="text-zinc-400 mx-auto mb-3" />
                  <p className="font-bold text-zinc-900">Trascina qui il file o clicca per selezionarlo</p>
                  <p className="text-xs text-zinc-500 mt-1">PDF o Word (.docx) · max 10 MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="import-file-input"
                  />
                </div>
                {uploadError && (
                  <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg" data-testid="import-error">
                    {uploadError}
                  </div>
                )}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  {[
                    { icon: FileText, t: '1. Carichi il file', d: 'PDF nativo o Word' },
                    { icon: Sparkle, t: '2. L\'AI lo legge', d: 'Gemini identifica i campi' },
                    { icon: Check, t: '3. Personalizzi e salvi', d: 'Rinomini o rimuovi placeholder' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white border border-zinc-200 rounded-xl p-3">
                      <s.icon size={18} className="text-teal-600 mb-2" />
                      <p className="font-bold text-zinc-900">{s.t}</p>
                      <p className="text-zinc-500 text-[11px]">{s.d}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 'parsing' && (
              <motion.div key="parsing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-12 text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                  className="w-14 h-14 border-4 border-teal-100 border-t-teal-600 rounded-full mx-auto mb-5"
                />
                <p className="font-bold text-zinc-900 text-lg">L'AI sta leggendo il contratto</p>
                <p className="text-sm text-zinc-500 mt-1">Estrazione testo · Identificazione campi · Ricostruzione HTML</p>
                <p className="text-xs text-zinc-400 mt-3">Solitamente richiede 10-30 secondi</p>
              </motion.div>
            )}

            {step === 'edit' && parsed && (
              <motion.div key="edit" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-2.5">
                    <p className="text-teal-900 font-bold">{parsed.fields.length} campi trovati</p>
                    <p className="text-teal-700">Placeholder identificati dall'AI</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    <p className="text-amber-900 font-bold">{parsed.fields.filter((f) => f.assigned_to === 'host').length} campi Host</p>
                    <p className="text-amber-700">Compilati da te</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                    <p className="text-blue-900 font-bold">{parsed.fields.filter((f) => f.assigned_to === 'guest').length} campi Ospite</p>
                    <p className="text-blue-700">Compilati dall'ospite</p>
                  </div>
                </div>

                {parsed.used_ocr && (
                  <div className="bg-purple-50 border border-purple-200 text-purple-800 text-xs p-2.5 rounded-lg">
                    <strong>OCR:</strong> il PDF era una scansione, l'AI ha letto le pagine come immagini. Se noti errori nel testo, usa "Modifica HTML" qui sotto.
                  </div>
                )}

                {parsed.translations && Object.keys(parsed.translations).length >= 2 && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs p-2.5 rounded-lg flex items-start gap-2" data-testid="translations-badge">
                    <span className="text-lg leading-none">🌐</span>
                    <div>
                      <p className="font-bold">Tradotto automaticamente in {Object.keys(parsed.translations).length} lingue</p>
                      <p className="text-emerald-700 mt-0.5">
                        Lingua sorgente: <strong>{parsed.source_lang || 'ITA'}</strong> · Traduzioni:{' '}
                        {Object.keys(parsed.translations)
                          .filter((k) => k !== (parsed.source_lang || 'ITA'))
                          .join(', ')}
                        . I placeholder <code className="bg-white/60 px-1 rounded">{'{{campo}}'}</code> sono preservati in tutte le versioni.
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Nome del template</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="rounded-lg"
                    placeholder="Es. Contratto importato Personale"
                    data-testid="import-name-input"
                  />
                </div>

                {/* Preview */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Anteprima contratto</Label>
                    <button
                      onClick={() => setEditingHtml(!editingHtml)}
                      className="text-xs text-teal-700 hover:text-teal-900 font-semibold flex items-center gap-1"
                      data-testid="toggle-edit-html-btn"
                    >
                      <PencilSimple size={12} />
                      {editingHtml ? 'Anteprima' : 'Modifica testo'}
                    </button>
                  </div>
                  {editingHtml ? (
                    <textarea
                      value={parsed.html}
                      onChange={(e) => setParsed((p) => ({ ...p, html: e.target.value }))}
                      className="w-full h-64 p-3 border border-zinc-300 rounded-lg font-mono text-xs"
                      data-testid="import-html-textarea"
                    />
                  ) : (
                    <div className="max-h-64 overflow-y-auto bg-white border border-zinc-200 rounded-xl p-4" data-testid="import-preview">
                      {renderPreview()}
                    </div>
                  )}
                </div>

                {/* Fields */}
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Campi da compilare</Label>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {parsed.fields.map((f) => (
                      <div key={f.key} className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl p-2.5" data-testid={`import-field-${f.key}`}>
                        <span className={`inline-flex w-8 h-8 items-center justify-center rounded-full ${f.assigned_to === 'guest' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'} flex-shrink-0`}>
                          {f.assigned_to === 'guest' ? <UserSquare size={16} weight="bold" /> : <User size={16} weight="bold" />}
                        </span>
                        {editingKey === f.key ? (
                          <input
                            autoFocus
                            defaultValue={f.label}
                            onBlur={(e) => { updateField(f.key, { label: e.target.value }); setEditingKey(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { updateField(f.key, { label: e.target.value }); setEditingKey(null); } }}
                            className="flex-1 border border-zinc-300 px-2 py-1 text-sm rounded"
                            data-testid={`import-field-rename-${f.key}`}
                          />
                        ) : (
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-zinc-900 truncate">{f.label}</p>
                            <p className="text-[10px] text-zinc-400 font-mono">{'{{' + f.key + '}}'}</p>
                          </div>
                        )}
                        <select
                          value={f.assigned_to}
                          onChange={(e) => updateField(f.key, { assigned_to: e.target.value })}
                          className="text-xs border border-zinc-300 rounded px-2 py-1"
                          data-testid={`import-field-role-${f.key}`}
                        >
                          <option value="host">Host</option>
                          <option value="guest">Ospite</option>
                        </select>
                        <button onClick={() => setEditingKey(f.key)} className="p-1 text-zinc-400 hover:text-zinc-900" aria-label="Rinomina" data-testid={`import-field-edit-${f.key}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => deleteField(f.key)} className="p-1 text-zinc-400 hover:text-red-600" aria-label="Elimina" data-testid={`import-field-delete-${f.key}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                    {parsed.fields.length === 0 && (
                      <p className="text-sm text-zinc-400 italic text-center py-4">Nessun campo da compilare (contratto completamente statico).</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 'saving' && (
              <motion.div key="saving" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-8 text-center">
                <div className="w-10 h-10 border-4 border-teal-100 border-t-teal-600 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-zinc-600">Salvataggio in corso…</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {step === 'edit' && (
          <div className="border-t border-zinc-100 px-6 py-4 flex items-center justify-between gap-3 bg-white">
            <button
              onClick={() => reset()}
              className="text-sm text-zinc-500 hover:text-zinc-900 font-semibold"
              data-testid="import-restart-btn"
            >
              Ricomincia con un altro file
            </button>
            <Button
              onClick={handleSave}
              className="bg-zinc-900 hover:bg-zinc-800 text-white font-bold rounded-xl px-5"
              data-testid="import-save-btn"
            >
              Salva template <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
